/*
 * Purpose: L1 outcome interpretation — Super Intent before full plan
 * Last modified: 2026-07-10 | Agent
 * Completeness: 92
 */
import { hasResolvedSecret, resolveSecret } from '@/lib/resolveSecret';
import { readEnvOr } from '@/lib/runtimeEnv';
import {
  intentInterpretationSchema,
  type IntentInterpretationPayload,
} from './intentInterpretSchema';
import type { ComposeMode } from './missionTypes';
import { MISSION_REQUEST_FIELD_CATALOG } from '@/lib/missionRequestFields';

const MODEL = readEnvOr('VITE_OPENAI_MODEL', 'gpt-4o-mini');

export interface IntentInterpretation extends IntentInterpretationPayload {
  rawOutcome: string;
  plannerSource: 'llm' | 'rules';
  suggestedComposeMode: ComposeMode;
}


function ruleBasedInterpret(rawOutcome: string): IntentInterpretation {
  const lower = rawOutcome.toLowerCase();
  const needsGmail = /\b(email|gmail|inbox|receipt|job alert|linkedin.*email)\b/i.test(lower);
  const needsLinkedIn = /\blinkedin\b/i.test(lower);
  const needsOpenAi = /\b(resume|cover letter|tailor|ai|gpt)\b/i.test(lower);
  const isFollowUp = /\b(follow[\s-]?up|remind|over time|days? later)\b/i.test(lower);
  const isComplex =
    needsGmail && needsLinkedIn && needsOpenAi || lower.split(/\s+/).length > 35;

  const suggestedComposeMode: ComposeMode =
    isComplex || isFollowUp || needsLinkedIn ? 'guided' : 'express';

  const gaps: string[] = [];
  if (needsOpenAi && !hasResolvedSecret('OPENAI_API_KEY')) {
    gaps.push('OpenAI API key not configured — Guided mode recommended');
  }
  if (needsGmail) gaps.push('Gmail or inbound email path may be required');
  if (needsLinkedIn) gaps.push('LinkedIn blocks need explicit blockIntent and taskOutcome for MCP — set on each block from the GOAL.');
  if (/\b(score|scoring|rubric|fit)\b/i.test(lower) && /\b(job|role|position)\b/i.test(lower)) {
    gaps.push('Details on how the user scores job fit');
  }
  if (/\b(tailor|adapt|customize)\b/i.test(lower)) {
    gaps.push('Details on how outputs should be adapted for each case');
  }

  return {
    rawOutcome,
    interpretedGoal: rawOutcome.length > 120 ? `${rawOutcome.slice(0, 117)}…` : rawOutcome,
    confidence: needsLinkedIn || isComplex ? 0.68 : 0.72,
    suggestedComposeMode,
    assumptions: [
      'AutoBlocks will assemble blocks — you confirm before anything runs live.',
    ],
    gaps,
    needsGmail,
    needsLinkedIn,
    needsOpenAi,
    plannerSource: 'rules',
  };
}

async function llmInterpret(
  rawOutcome: string,
  options?: { missionKnowledgeContext?: string; missionDossierContext?: string }
): Promise<IntentInterpretation | null> {
  const apiKey = resolveSecret('OPENAI_API_KEY');
  if (!apiKey) return null;

  const knowledgeBlock = options?.missionKnowledgeContext?.trim()
    ? `\n\nMISSION KNOWLEDGE (user-provided):\n${options.missionKnowledgeContext.trim()}\n`
    : '';
  const dossierBlock = options?.missionDossierContext?.trim()
    ? `\n\n${options.missionDossierContext.trim()}\n`
    : '';

  const system = `You interpret user OUTCOMES for AutoBlocks (workflow automation). Return JSON only.
Fields: interpretedGoal (plain English), confidence 0-1, suggestedComposeMode "express"|"guided",
assumptions[], gaps[], needsGmail?, needsLinkedIn?, needsOpenAi?,
knowledgeRequests? — compose a mission request PAGE for mission-specific gaps (not infra OAuth).

${MISSION_REQUEST_FIELD_CATALOG}

For each knowledge gap about user process/preferences/docs, add knowledgeRequests[] with 2–6 fields.
Use vault_secret only when the Mission truly needs a password/token stored encrypted (vaultSecretName UPPER_SNAKE).
Use file_text for long paste-ins. Use multiselect/tags/boolean/rating when helpful.
Infra gaps (Gmail OAuth, OpenAI key missing) stay in gaps[] only — NOT in knowledgeRequests.
Each knowledgeRequests block is scoped to this Mission session — never assume shared libraries.

You may honestly propose any approach to complete the Mission — be creative and thorough. Flag infra or ToS risks in gaps[]; execution of risky items waits for human approval.

If MISSION DOSSIER is provided, treat it as read-only audit history — continue from prior AI actions, do not contradict it.

Use guided when LinkedIn, MCP, or multi-step outcomes need per-block intent; express only for simple notify/watch flows.`;

  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: MODEL,
        temperature: 0.2,
        response_format: { type: 'json_object' },
        messages: [
          { role: 'system', content: system },
          { role: 'user', content: `OUTCOME:\n${rawOutcome}${knowledgeBlock}${dossierBlock}` },
        ],
      }),
    });

    if (!response.ok) return null;
    const data = await response.json();
    const content = data?.choices?.[0]?.message?.content;
    if (!content) return null;

    const parsed = intentInterpretationSchema.parse(JSON.parse(content));
    return {
      ...parsed,
      rawOutcome,
      suggestedComposeMode: parsed.suggestedComposeMode ?? 'guided',
      plannerSource: 'llm',
    };
  } catch {
    return null;
  }
}

export async function interpretOutcomeAsync(
  rawOutcome: string,
  options?: { missionKnowledgeContext?: string; missionDossierContext?: string }
): Promise<IntentInterpretation> {
  const trimmed = rawOutcome.trim();
  if (!trimmed) {
    throw new Error('Enter an outcome to interpret');
  }

  const fromLlm = await llmInterpret(trimmed, options);
  if (fromLlm) return fromLlm;
  return ruleBasedInterpret(trimmed);
}
